import React, { useState, useEffect } from 'react';
import { X, Calendar as CalendarIcon, Clock } from 'lucide-react';
import { DayPicker } from 'react-day-picker';
import { format } from 'date-fns';
import 'react-day-picker/dist/style.css';
import axios from 'axios';
import ConsentForm from './ConsentForm';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const BookingModal = ({ isOpen, onClose, initialServices = [] }) => {
  const initialKey = initialServices.join('|');
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    service: '',
    preferred_date: '',
    message: ''
  });
  const [selectedServices, setSelectedServices] = useState([]);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedTime, setSelectedTime] = useState('');
  const [showCalendar, setShowCalendar] = useState(false);
  const [showConsent, setShowConsent] = useState(false);
  const [consentData, setConsentData] = useState(null);

  // Updated IV Cocktails list (reordered)
  const ivCocktails = [
    'Myers Cocktail',
    'Hangover Drip',
    'Hydrate',
    'Athletic Drip',
    'Infusion Beauty',
    'Immune Drip',
    'Infusion Migraine'
  ];

  // Updated IM Injections list (with new additions)
  const imInjections = [
    'B12 Energy Shot',
    'Vitamin D3 Shot',
    'Glutathione',
    'Arginine Injection',
    'Lysine Injection',
    'Pyridoxine (B6) Injection',
    'Zofran (Anti-Nausea)',
    'Ketorolac (Pain Relief)'
  ];

  // Generate time slots - EVERY OTHER HOUR for proper spacing
  const generateTimeSlots = () => {
    const slots = [];
    // Start at 9 AM, only odd hours (9, 11, 1, 3, 5, 7, 9)
    const availableHours = [9, 11, 13, 15, 17, 19, 21];
    
    for (let hour of availableHours) {
      const time12hr = hour > 12 ? hour - 12 : hour;
      const ampm = hour >= 12 ? 'PM' : 'AM';
      const displayHour = time12hr === 0 ? 12 : time12hr;
      slots.push(`${displayHour}:00 ${ampm}`);
    }
    return slots;
  };

  const timeSlots = generateTimeSlots();

  const toggleService = (service) => {
    setSelectedServices((prev) =>
      prev.includes(service)
        ? prev.filter((s) => s !== service)
        : [...prev, service]
    );
  };

  useEffect(() => {
    if (isOpen) {
      setSuccess(false);
      setError('');
      setSelectedServices(initialKey ? initialKey.split('|') : []);
      setSelectedDate(null);
      setSelectedTime('');
      setFormData({
        name: '',
        email: '',
        phone: '',
        service: '',
        preferred_date: '',
        message: ''
      });
    }
  }, [isOpen, initialKey]);

  // Update preferred_date when date and time are selected
  useEffect(() => {
    if (selectedDate && selectedTime) {
      const formattedDate = format(selectedDate, 'MMMM d, yyyy');
      setFormData({ ...formData, preferred_date: `${formattedDate} at ${selectedTime}` });
    }
  }, [selectedDate, selectedTime]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (selectedServices.length === 0) {
      setError('Please select at least one treatment (IV Cocktail or IM Injection).');
      return;
    }

    if (!selectedDate || !selectedTime) {
      setError('Please select both date and time for your appointment.');
      return;
    }

    // Show consent form instead of direct submission
    setShowConsent(true);
  };

  const handleConsentAccept = async (consent) => {
    setConsentData(consent);
    setShowConsent(false);
    setLoading(true);
    setError('');

    try {
      const bookingPayload = {
        ...formData,
        service: selectedServices.join(', '),
        consent_signed: true,
        consent_signature: consent.signature,
        consent_timestamp: consent.timestamp
      };

      const response = await axios.post(`${API}/booking`, bookingPayload);
      if (response.data.success) {
        setSuccess(true);
        setFormData({
          name: '',
          email: '',
          phone: '',
          service: '',
          preferred_date: '',
          message: ''
        });
        setSelectedServices([]);
        setSelectedDate(null);
        setSelectedTime('');
        setConsentData(null);
        setTimeout(() => {
          onClose();
        }, 6000);
      }
    } catch (err) {
      const status = err?.response?.status;
      if (status === 429) {
        setError(
          'We received many booking attempts from your network in a short window. ' +
          'Please wait a few minutes and try again, or call us directly at 727-503-2572.'
        );
      } else if (status === 422) {
        setError(
          'Some information looks incomplete or invalid. Please double-check your name, ' +
          'email, phone, date, and time, then try again.'
        );
      } else if (!navigator.onLine) {
        setError(
          'You appear to be offline. Please check your connection and try again.'
        );
      } else {
        setError(
          'Failed to submit booking. Please try again or call us directly at 727-503-2572.'
        );
      }
    } finally {
      setLoading(false);
    }
  };

  const handleConsentCancel = () => {
    setShowConsent(false);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm" data-testid="booking-modal">
      <div className="bg-gray-900 rounded-2xl max-w-2xl w-full p-6 relative border border-yellow-500/30 max-h-[90vh] overflow-y-auto">
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-gray-400 hover:text-yellow-400 transition-colors z-10"
          data-testid="close-booking-modal"
        >
          <X className="w-6 h-6" />
        </button>

        <h2 className="text-2xl font-bold text-yellow-400 mb-2">Book Your Treatment</h2>
        <p className="text-gray-400 text-sm mb-6">Select your date, time, and treatment details</p>

        {success ? (
          <div className="text-center py-8 px-2" data-testid="booking-success-message">
            <div className="text-6xl mb-4">✅</div>
            <h3 className="text-2xl font-bold text-yellow-400 mb-3">Thank You — Your Request Has Been Received</h3>
            <p className="text-gray-200 leading-relaxed mb-3">
              A member of our team will reach out to you shortly via <span className="text-yellow-400 font-semibold">text message</span> to coordinate one additional step before your appointment:
            </p>
            <p className="text-gray-300 leading-relaxed mb-3">
              A brief <span className="text-yellow-400 font-semibold">telehealth consultation</span> with one of our licensed providers to medically clear you for treatment and ensure the safest, most personalized experience.
            </p>
            <p className="text-gray-400 text-sm italic">
              We appreciate your trust in EliteIV Lounge. Please keep an eye on your phone.
            </p>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Personal Info */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Name *</label>
                <input
                  type="text"
                  required
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full px-4 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white focus:ring-2 focus:ring-yellow-400 focus:border-transparent"
                  data-testid="booking-name-input"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Phone *</label>
                <input
                  type="tel"
                  required
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  className="w-full px-4 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white focus:ring-2 focus:ring-yellow-400 focus:border-transparent"
                  data-testid="booking-phone-input"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Email *</label>
              <input
                type="email"
                required
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                className="w-full px-4 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white focus:ring-2 focus:ring-yellow-400 focus:border-transparent"
                data-testid="booking-email-input"
              />
            </div>

            {/* Service Selection - Multi-select (IV + IM both available) */}
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">
                Select Your Treatment(s) *
              </label>
              <p className="text-xs text-gray-500 mb-3">
                Select one or more — you can combine IV cocktails and IM injections in a single appointment.
              </p>

              {/* IV Cocktails Group */}
              <div className="bg-gray-800/50 border border-gray-700 rounded-lg p-4 mb-3">
                <div className="text-yellow-400 font-semibold text-sm mb-3 uppercase tracking-wide">
                  IV Cocktails
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {ivCocktails.map((service) => {
                    const checked = selectedServices.includes(service);
                    return (
                      <label
                        key={service}
                        className={`flex items-center gap-2 px-3 py-2 rounded-md cursor-pointer transition-all border ${
                          checked
                            ? 'bg-yellow-400/10 border-yellow-400/60 text-white'
                            : 'bg-gray-900/40 border-gray-700 text-gray-300 hover:border-yellow-400/40'
                        }`}
                        data-testid={`service-iv-${service.toLowerCase().replace(/\s+/g, '-')}`}
                      >
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={() => toggleService(service)}
                          className="accent-yellow-400 w-4 h-4"
                        />
                        <span className="text-sm">{service}</span>
                      </label>
                    );
                  })}
                </div>
              </div>

              {/* IM Injections Group */}
              <div className="bg-gray-800/50 border border-gray-700 rounded-lg p-4">
                <div className="text-yellow-400 font-semibold text-sm mb-3 uppercase tracking-wide">
                  IM Injections
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {imInjections.map((service) => {
                    const checked = selectedServices.includes(service);
                    return (
                      <label
                        key={service}
                        className={`flex items-center gap-2 px-3 py-2 rounded-md cursor-pointer transition-all border ${
                          checked
                            ? 'bg-yellow-400/10 border-yellow-400/60 text-white'
                            : 'bg-gray-900/40 border-gray-700 text-gray-300 hover:border-yellow-400/40'
                        }`}
                        data-testid={`service-im-${service.toLowerCase().replace(/\s+/g, '-')}`}
                      >
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={() => toggleService(service)}
                          className="accent-yellow-400 w-4 h-4"
                        />
                        <span className="text-sm">{service}</span>
                      </label>
                    );
                  })}
                </div>
              </div>

              {/* Selected Services Summary */}
              {selectedServices.length > 0 && (
                <div className="mt-3 flex flex-wrap gap-2" data-testid="selected-services-summary">
                  {selectedServices.map((s) => (
                    <span
                      key={s}
                      className="inline-flex items-center gap-1 bg-yellow-400 text-black text-xs font-semibold px-3 py-1 rounded-full"
                    >
                      {s}
                      <button
                        type="button"
                        onClick={() => toggleService(s)}
                        className="hover:text-red-700"
                        aria-label={`Remove ${s}`}
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </span>
                  ))}
                </div>
              )}
            </div>

            {/* Date and Time Selection */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Date Picker */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Select Date *</label>
                <button
                  type="button"
                  onClick={() => setShowCalendar(!showCalendar)}
                  className="w-full px-4 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white hover:border-yellow-400 transition-colors flex items-center justify-between"
                  data-testid="date-picker-btn"
                >
                  <span className="flex items-center gap-2">
                    <CalendarIcon className="w-4 h-4 text-yellow-400" />
                    {selectedDate ? format(selectedDate, 'MMM d, yyyy') : 'Choose date...'}
                  </span>
                </button>
                {showCalendar && (
                  <div className="absolute z-20 mt-2 bg-gray-800 border border-yellow-500/30 rounded-lg p-4 shadow-xl">
                    <DayPicker
                      mode="single"
                      selected={selectedDate}
                      onSelect={(date) => {
                        setSelectedDate(date);
                        setShowCalendar(false);
                      }}
                      disabled={{ before: new Date() }}
                      className="rdp-custom"
                      modifiersClassNames={{
                        selected: 'rdp-selected',
                        today: 'rdp-today'
                      }}
                    />
                  </div>
                )}
              </div>

              {/* Time Picker */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Select Time *</label>
                <select
                  required
                  value={selectedTime}
                  onChange={(e) => setSelectedTime(e.target.value)}
                  className="w-full px-4 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white focus:ring-2 focus:ring-yellow-400 focus:border-transparent"
                  data-testid="time-picker-select"
                >
                  <option value="">Choose time...</option>
                  {timeSlots.map((time) => (
                    <option key={time} value={time}>
                      {time}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* Selected Appointment Display */}
            {selectedDate && selectedTime && (
              <div className="bg-yellow-400/10 border border-yellow-400/30 rounded-lg p-4" data-testid="appointment-summary">
                <div className="flex items-center gap-2 text-yellow-400 mb-1">
                  <Clock className="w-4 h-4" />
                  <span className="font-semibold">Your Appointment:</span>
                </div>
                <p className="text-white font-medium">
                  {format(selectedDate, 'EEEE, MMMM d, yyyy')} at {selectedTime}
                </p>
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Additional Notes</label>
              <textarea
                value={formData.message}
                onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                rows="3"
                placeholder="Any special requests or health information we should know?"
                className="w-full px-4 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white focus:ring-2 focus:ring-yellow-400 focus:border-transparent"
                data-testid="booking-message-input"
              />
            </div>

            {error && (
              <div className="text-red-400 text-sm bg-red-400/10 border border-red-400/30 rounded-lg p-3" data-testid="booking-error-message">
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              className="w-full bg-gradient-to-r from-yellow-400 to-yellow-600 text-black font-semibold py-3 rounded-lg hover:from-yellow-500 hover:to-yellow-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="booking-submit-btn"
            >
              {loading ? 'Submitting...' : 'Confirm Booking'}
            </button>
          </form>
        )}
      </div>

      {/* Custom CSS for DayPicker */}
      <style>{`
        .rdp-custom {
          --rdp-cell-size: 40px;
          --rdp-accent-color: #FFD700;
          --rdp-background-color: rgba(255, 215, 0, 0.1);
          color: white;
        }
        .rdp-day_selected {
          background-color: #FFD700 !important;
          color: black !important;
          font-weight: bold;
        }
        .rdp-day_today {
          font-weight: bold;
          color: #FFD700;
        }
        .rdp-button:hover:not([disabled]):not(.rdp-day_selected) {
          background-color: rgba(255, 215, 0, 0.2);
        }
        .rdp-head_cell {
          color: #C0C0C0;
          font-weight: 600;
        }
        .rdp-caption {
          color: #FFD700;
          font-weight: bold;
        }
      `}</style>

      {/* Consent Form Modal */}
      {showConsent && (
        <ConsentForm
          onAccept={handleConsentAccept}
          onCancel={handleConsentCancel}
          customerName={formData.name}
        />
      )}
    </div>
  );
};

export default BookingModal;
